import { DataField } from '@formily/core'
import {
  RecursionField,
  Schema,
  observer,
  useField,
  useForm
} from '@formily/react'
import { TableColumnGroupType, TableColumnsType, TableColumnType } from 'antd'
import { createStyles } from 'antd-style'

import { toArray } from '@eastcoal/common/utils/array'

import { getColumnPropsFromSchema } from '../utils/schema'

const useStyles = createStyles(({ token }) => ({
  asterisk: {
    display: 'inline-block',
    lineHeight: 1,
    marginInlineEnd: 2,
    verticalAlign: -2,
    color: token.colorError
  }
}))

const BuiltinRender = observer(() => {
  const field = useField<DataField>()
  return field.value
})

/**
 * 从 schema 中生成 columns
 */

export const useEditableTableColumns = (schema: Schema) => {
  const form = useForm()

  const { values, readPretty } = form

  const scope = {
    $form: form,
    $values: values
  }

  const { styles } = useStyles()

  const resolveColumns = (schemas?: Schema | Schema[]) => {
    if (!schemas) {
      return undefined
    }

    const columns: TableColumnsType<any> = []

    toArray(schemas).forEach(schema => {
      const display = Schema.compile(schema['x-display'], scope)

      if (display === 'none') {
        return
      }

      const wholeKey = schema.name

      const columnProps = getColumnPropsFromSchema(schema)

      const children = schema.items ? resolveColumns(schema.items) : undefined

      const title = Schema.compile(schema['title'], scope)

      const required =
        schema['required'] ||
        toArray(schema['x-validator']).some(item => item.required)

      const asterisk = required && !readPretty && (
        <span className={styles.asterisk}>*</span>
      )

      const shouldAlignRight =
        schema.type === 'number' && !schema['x-component']

      const column: TableColumnType<any> | TableColumnGroupType<any> = {
        align: shouldAlignRight ? 'right' : 'center',
        ...columnProps,
        key: wholeKey,
        dataIndex: wholeKey,
        title: (
          <span>
            {asterisk}
            {title}
          </span>
        )
      }

      if (children?.length) {
        ;(column as TableColumnGroupType<any>).children = children
      } else {
        column.render = () => {
          return (
            <RecursionField
              name={wholeKey}
              schema={{
                ...schema,
                'x-component': schema['x-component'] ?? BuiltinRender
              }}
            />
          )
        }
      }
      columns.push(column)
    })

    return columns
  }

  return resolveColumns(schema.items)
}
